import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LogissimoGradientBar } from "@/components/ui/logissimo-gradient-bar";
import { login, getSsoUrl } from "@/lib/api/auth";
import { useAuthStore } from "@/lib/auth/store";
import { getErrorMessage } from "@/lib/api/client";

import cityBackground from "@/assets/city_logissimo.jpg";
import logoRvb from "@/assets/logo-rvb.png";
import logoReserve from "@/assets/logo-reserve.png";

export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
})

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "Le mot de passe doit contenir au moins 8 caractères"),
});

type LoginForm = z.infer<typeof loginSchema>;

const REMEMBERED_EMAIL_STORAGE_KEY = "logissimo:remembered-email";

function readRememberedEmail(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(REMEMBERED_EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeRememberedEmail(email: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (email && email.length > 0) {
      window.localStorage.setItem(REMEMBERED_EMAIL_STORAGE_KEY, email);
    } else {
      window.localStorage.removeItem(REMEMBERED_EMAIL_STORAGE_KEY);
    }
  } catch {
    // localStorage indisponible (mode navigation privée strict, quota plein, etc.) — silencieux
  }
}

// Enhanced animated hexagon component with organic movement
function FloatingHexagon({ 
  className, 
  delay = 0,
  duration = 20,
  size = 60,
  path = "vertical"
}: { 
  className?: string;
  delay?: number;
  duration?: number;
  size?: number;
  path?: "vertical" | "diagonal" | "curved";
}) {
  const pathVariants = {
    vertical: {
      y: [0, -100, -200, -300],
      x: [0, 0, 0, 0],
    },
    diagonal: {
      y: [0, -80, -160, -240],
      x: [0, 30, 10, 40],
    },
    curved: {
      y: [0, -60, -140, -220],
      x: [0, 40, -20, 30],
    },
  };

  return (
    <motion.div
      className={`absolute pointer-events-none ${className}`}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0, 0.2, 0.15, 0],
        scale: [0.8, 1, 1.1, 0.9],
        rotate: [0, 90, 180, 270],
        ...pathVariants[path],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <svg width={size} height={size * 1.15} viewBox="0 0 60 69" fill="none">
        <path
          d="M30 0L57.3013 15.75V47.25L30 63L2.69873 47.25V15.75L30 0Z"
          stroke={`url(#hexGradient-${delay})`}
          strokeWidth="2"
          fill="none"
        />
        <defs>
          <linearGradient id={`hexGradient-${delay}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFCC00" />
            <stop offset="50%" stopColor="#00B2A9" />
            <stop offset="100%" stopColor="#0066B3" />
          </linearGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

// Success checkmark animation
function SuccessCheckmark() {
  return (
    <motion.div
      className="flex flex-col items-center gap-4"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 15 }}
    >
      <motion.div 
        className="w-20 h-20 rounded-full bg-gradient-to-br from-laposte-teal to-laposte-teal-dark flex items-center justify-center shadow-lg shadow-laposte-teal/30"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
      >
        <motion.div
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Check className="w-10 h-10 text-white" strokeWidth={3} />
        </motion.div>
      </motion.div>
      <motion.p 
        className="text-xl font-semibold text-foreground"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        Connexion réussie !
      </motion.p>
    </motion.div>
  );
}

// Shake animation variant
const shakeAnimation = {
  x: [0, -10, 10, -10, 10, -5, 5, 0],
  transition: { duration: 0.5 }
};

function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const rememberedEmail = readRememberedEmail();
  const [rememberMe, setRememberMe] = useState(() => rememberedEmail !== null);
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  // Mouse position for parallax effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    mouseX.set(x * 15);
    mouseY.set(y * 15);
  }, [mouseX, mouseY]);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: rememberedEmail ?? "",
      password: "",
    },
  });

  // Si l'email est pré-rempli depuis "Se souvenir de moi", on positionne le focus sur le mot de passe
  useEffect(() => {
    if (rememberedEmail) {
      setFocus("password");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger shake on validation errors
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShouldShake(true);
      const timer = setTimeout(() => setShouldShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [errors]);

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const response = await login(data.email, data.password);

      if (!response.success || !response.data?.user || !response.data?.tokens) {
        throw new Error(response.message || "Erreur serveur");
      }

      const { user, tokens } = response.data;
      setAuth(user, tokens);

      // Persistance de l'email uniquement (jamais le mot de passe — c'est le rôle du
      // gestionnaire de mots de passe du navigateur via autoComplete="current-password").
      writeRememberedEmail(rememberMe ? data.email : null);

      setIsSuccess(true);

      toast.success("Connexion réussie", {
        description: `Bienvenue, ${user.full_name || user.email}`,
      });

      setTimeout(() => {
        const redirectPath = user.role === "admin" ? "/" : "/simulators";
        navigate({ to: redirectPath });
      }, 1200);
    } catch (error) {
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
      toast.error("Erreur de connexion", {
        description: getErrorMessage(error),
      });
      setIsLoading(false);
    }
  };

  const handleSsoLogin = async () => {
    setIsSsoLoading(true);
    try {
      const response = await getSsoUrl();
      if (!response.success || !response.data?.url) {
        throw new Error("Impossible de générer l'URL SSO");
      }
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Erreur SSO", {
        description: getErrorMessage(error),
      });
      setIsSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image with parallax effect */}
      <div 
        className="hidden lg:flex lg:w-[55%] relative overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        <motion.img
          src={cityBackground}
          alt="Logissimo"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            scale: 1.1,
            x: springX,
            y: springY,
          }}
        />
        {/* Multi-layer gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-laposte-navy/90 via-laposte-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
        
        {/* Animated decorative gradient line */}
        <LogissimoGradientBar 
          variant="animated" 
          height="lg" 
          delay={0.5}
          className="absolute bottom-0 left-0 right-0" 
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={logoReserve} alt="Logissimo" className="h-14 w-auto drop-shadow-lg" />
          </motion.div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <motion.div 
                  className="w-16 h-1 rounded-full"
                  style={{ 
                    background: "linear-gradient(90deg, #FFCC00, #00B2A9, #FFCC00)",
                    backgroundSize: "200% 100%",
                  }}
                  initial={{ width: 0 }}
                  animate={{ 
                    width: 64,
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                  }}
                  transition={{ 
                    width: { duration: 0.6, delay: 0.6 },
                    backgroundPosition: { duration: 3, repeat: Infinity, ease: "linear", delay: 1.2 }
                  }}
                />
                <h2 className="text-5xl font-bold leading-tight tracking-tight">
                  Logissimo<br />
                  <span className="text-laposte-yellow">Pricemaker</span>
                </h2>
              </div>
              <p className="text-xl text-white/80 max-w-lg leading-relaxed">
                Établissez vos cotations et créez des propositions commerciales personnalisées.
              </p>
            </motion.div>

          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="text-sm text-white/50"
          >
            © {new Date().getFullYear()} Logissimo - Tous droits réservés
          </motion.p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative overflow-hidden bg-background">
        {/* Animated background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-laposte-blue/5" />
        
        {/* Mobile header gradient */}
        <motion.div 
          className="lg:hidden absolute top-0 left-0 right-0 h-32 gradient-logissimo opacity-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1 }}
        />
        
        {/* Enhanced floating hexagons with varied paths */}
        <FloatingHexagon className="left-[10%] bottom-[20%]" delay={0} size={40} path="vertical" />
        <FloatingHexagon className="right-[15%] bottom-[30%]" delay={5} size={30} path="diagonal" />
        <FloatingHexagon className="left-[20%] bottom-[10%]" delay={10} size={50} path="curved" />
        <FloatingHexagon className="right-[25%] bottom-[15%]" delay={15} size={35} path="diagonal" />
        <FloatingHexagon className="left-[5%] bottom-[40%]" delay={8} size={25} path="curved" />
        <FloatingHexagon className="right-[8%] bottom-[50%]" delay={12} size={45} path="vertical" />
        
        {/* Gradient orbs */}
        <motion.div 
          className="absolute top-1/4 -left-32 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #FFCC00 0%, transparent 70%)" }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(circle, #00B2A9 0%, transparent 70%)" }}
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.div 
          className="absolute top-1/2 right-0 w-64 h-64 rounded-full blur-3xl opacity-10"
          style={{ background: "radial-gradient(circle, #0066B3 0%, transparent 70%)" }}
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 4 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md relative z-10"
        >
          {/* Logo for mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-10 lg:hidden flex justify-center"
          >
            <img src={logoRvb} alt="Logissimo" className="h-12 w-auto" />
          </motion.div>

          {/* Card with enhanced styling and shake animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={shouldShake ? shakeAnimation : { opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl shadow-black/5 dark:shadow-black/20"
          >
            <AnimatePresence mode="wait">
              {isSuccess ? (
                <motion.div
                  key="success"
                  className="py-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <SuccessCheckmark />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  {/* Header */}
                  <div className="text-center mb-8 space-y-2">
                    <motion.h1 
                      className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      Connexion
                    </motion.h1>
                    <motion.p 
                      className="text-muted-foreground"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      Connectez-vous pour accéder à vos simulateurs
                    </motion.p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <motion.div 
                      className="space-y-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                      <div className="relative group input-glow-wrapper">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-laposte-yellow transition-colors duration-200" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="jordan.guillonneau@live.fr"
                          autoFocus={!rememberedEmail}
                          autoComplete="email"
                          aria-describedby={errors.email ? "email-error" : undefined}
                          className="pl-11 h-12 bg-background/50 border-border/50 focus:border-laposte-yellow/50 focus:ring-laposte-yellow/20 focus:shadow-[0_0_0_4px_rgba(255,204,0,0.1),0_0_20px_rgba(255,204,0,0.05)] transition-all duration-200"
                          {...register("email")}
                        />
                      </div>
                      {errors.email && (
                        <motion.p 
                          id="email-error"
                          className="text-sm text-destructive flex items-center gap-1"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          role="alert"
                        >
                          {errors.email.message}
                        </motion.p>
                      )}
                    </motion.div>

                    <motion.div 
                      className="space-y-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
                      <div className="relative group input-glow-wrapper">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-laposte-yellow transition-colors duration-200" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          aria-describedby={errors.password ? "password-error" : undefined}
                          className="pl-11 pr-11 h-12 bg-background/50 border-border/50 focus:border-laposte-yellow/50 focus:ring-laposte-yellow/20 focus:shadow-[0_0_0_4px_rgba(255,204,0,0.1),0_0_20px_rgba(255,204,0,0.05)] transition-all duration-200"
                          {...register("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <motion.p 
                          id="password-error"
                          className="text-sm text-destructive"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          role="alert"
                        >
                          {errors.password.message}
                        </motion.p>
                      )}
                    </motion.div>

                    <motion.div 
                      className="flex items-center justify-between"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <Checkbox 
                          id="remember" 
                          checked={rememberMe}
                          onCheckedChange={(checked) => setRememberMe(checked === true)}
                          className="data-[state=checked]:bg-laposte-yellow data-[state=checked]:border-laposte-yellow"
                        />
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          Se souvenir de moi
                        </span>
                      </label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-laposte-teal hover:text-laposte-teal-dark transition-colors font-medium"
                      >
                        Mot de passe oublié ?
                      </Link>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                    >
                      <Button
                        type="submit"
                        className="w-full h-12 text-base font-semibold bg-gradient-to-r from-laposte-yellow to-laposte-yellow-dark hover:from-laposte-yellow-dark hover:to-laposte-yellow text-laposte-navy shadow-lg shadow-laposte-yellow/20 hover:shadow-laposte-yellow/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                        size="lg"
                        disabled={isLoading || isSsoLoading}
                      >
                        {isLoading ? (
                          <motion.div 
                            className="w-5 h-5 border-2 border-laposte-navy border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                        ) : (
                          <span className="flex items-center gap-2">
                            Se connecter
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>
                    </motion.div>
                  </form>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 }}
                    className="mt-6"
                  >
                    <div className="relative mb-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border/50" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card/80 px-3 text-muted-foreground">ou</span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-12 text-base font-semibold border-laposte-blue/30 hover:border-laposte-blue hover:bg-laposte-blue/5 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                      onClick={handleSsoLogin}
                      disabled={isLoading || isSsoLoading}
                    >
                      {isSsoLoading ? (
                        <motion.div
                          className="w-5 h-5 border-2 border-laposte-blue border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <span className="flex items-center gap-2">
                          Connexion SSO
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Footer for mobile */}
          <motion.p 
            className="text-center text-sm text-muted-foreground mt-8 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            © {new Date().getFullYear()} Logissimo Pricemaker
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
